import { Button, Space, Card, Table, Typography, Input, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { AuditTrail, AuditTrailPage } from "../models/models";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { getAuditTrail } from "../models/serverApi";

const PAGE_SIZE = 20;

export const AuditTrailTable: React.FC = () => {
    const [page, setPage] = useState<AuditTrailPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [userNameFilter, setUserNameFilter] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [jsonModalData, setJsonModalData] = useState<string | null>(null);

    const fetchData = (pageNum: number, uName = userNameFilter, act = actionFilter) => {
        setLoading(true);
        const skip = (pageNum - 1) * PAGE_SIZE;
        getAuditTrail(skip, PAGE_SIZE, uName || undefined, act || undefined)
            .then(data => {
                if (data) {
                    setPage(data);
                    setCurrentPage(pageNum);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchData(1);
    }, []);

    const handleTableChange = (
        pagination: any,
        filters: Record<string, (string | number | boolean)[] | null>
    ) => {
        const uName = (filters['userName']?.[0] as string) ?? "";
        const act = (filters['action']?.[0] as string) ?? "";
        setUserNameFilter(uName);
        setActionFilter(act);
        fetchData(1, uName, act);
    };

    const columns = [
        {
            title: 'Γεν. Κωδικός',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Χρήστης',
            dataIndex: 'userName',
            key: 'userName',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Αναζήτηση..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Φίλτρο
                        </Button>
                        <Button onClick={() => { clearFilters?.(); confirm(); }} size="small" style={{ width: 90 }}>
                            Επαναφορά
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
            filteredValue: userNameFilter ? [userNameFilter] : null,
        },
        {
            title: 'Πράξη',
            dataIndex: 'action',
            key: 'action',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Αναζήτηση..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Φίλτρο
                        </Button>
                        <Button onClick={() => { clearFilters?.(); confirm(); }} size="small" style={{ width: 90 }}>
                            Επαναφορά
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
            filteredValue: actionFilter ? [actionFilter] : null,
        },
        {
            title: 'Ημερομηνία',
            dataIndex: 'auditDate',
            key: 'auditDate',
            render: (value: any, record: AuditTrail) => {
                return dayjs(record.auditDate).format("LLLL");
            }
        },
        {
            title: 'Δεδομένα',
            key: 'data',
            render: (text: any, record: AuditTrail) => {
                if (!record.data) return <span>-</span>;

                let preview = "";
                try {
                    const parsed = JSON.parse(record.data);
                    const formatted = JSON.stringify(parsed, null, 2);
                    const lines = formatted.split("\n");
                    preview = lines.slice(0, 3).join("\n") + (lines.length > 3 ? "\n..." : "");
                } catch {
                    preview = record.data.substring(0, 60) + (record.data.length > 60 ? "..." : "");
                }

                return (
                    <Space direction="vertical" size={4}>
                        <Typography.Text code style={{ whiteSpace: "pre-wrap", fontSize: 11, maxWidth: 300 }}>
                            {preview}
                        </Typography.Text>
                        <Button size="small" type="primary" onClick={() => setJsonModalData(record.data)}>
                            Εμφάνιση
                        </Button>
                    </Space>
                );
            }
        },
    ];

    return (
        <>
            <Card title="Ιστορικό Ενεργειών (Audit Trail)" bordered hoverable className="always" style={{ marginTop: "24px", width: "100%" }}>
                <Table
                    columns={columns}
                    dataSource={page?.trails ?? []}
                    rowKey="id"
                    loading={loading}
                    onChange={handleTableChange}
                    pagination={{
                        position: ["topRight"],
                        responsive: true,
                        pageSize: PAGE_SIZE,
                        total: page?.totalCount ?? 0,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        onChange: (pageNum) => fetchData(pageNum, userNameFilter, actionFilter)
                    }}
                />
            </Card>

            <Modal
                title="Δεδομένα"
                open={jsonModalData !== null}
                onCancel={() => setJsonModalData(null)}
                footer={<Button onClick={() => setJsonModalData(null)}>Κλείσιμο</Button>}
                width={700}
            >
                <Input.TextArea
                    readOnly
                    autoSize={{ minRows: 6, maxRows: 24 }}
                    value={(() => {
                        if (!jsonModalData) return "";
                        try {
                            return JSON.stringify(JSON.parse(jsonModalData), null, 2);
                        } catch {
                            return jsonModalData;
                        }
                    })()}
                    style={{ fontFamily: "monospace", fontSize: 12 }}
                />
            </Modal>
        </>
    );
}
